import React from 'react'
import { Badge, EmptyState, type BadgeColor } from '@enamelai/design-system'
import { Icon as AlertIcon } from '@enamelai/design-system/icons/Alert'
import { Icon as ClockIcon } from '@enamelai/design-system/icons/Clock'
import { Icon as InfoIcon } from '@enamelai/design-system/icons/Info'
import NotificationCard from './NotificationCard.component'
import {
  Notification,
  NotificationPriority,
} from '@dataTypes/notification.types'
import './NotificationsStyle.css'

interface NotificationColumnProps {
  priority: NotificationPriority
  title: string
  count: number
  notifications: Notification[]
  readNotifications?: Set<string>
  onMarkAsRead?: (id: string) => void
  onAction?: (id: string) => void
}

const getBadgeColor = (priority: NotificationPriority): BadgeColor => {
  switch (priority) {
    case NotificationPriority.CRITICAL:
      return 'red'
    case NotificationPriority.URGENT:
      return 'orange'
    default:
      return 'blue'
  }
}

const BadgeIcon: React.FC<{ priority: NotificationPriority }> = ({
  priority,
}) => {
  switch (priority) {
    case NotificationPriority.CRITICAL:
      return <AlertIcon aria-hidden="true" />
    case NotificationPriority.URGENT:
      return <ClockIcon aria-hidden="true" />
    default:
      return <InfoIcon aria-hidden="true" />
  }
}

const NotificationColumn: React.FC<NotificationColumnProps> = ({
  priority,
  title,
  notifications,
  readNotifications = new Set(),
  onMarkAsRead,
  onAction,
}) => {
  return (
    <div className="notification-column">
      <Badge
        className="notification-column-badge"
        mode="tinted"
        color={getBadgeColor(priority)}
      >
        <span className="notification-column-badge-content">
          <BadgeIcon priority={priority} />
          {title}
        </span>
      </Badge>
      <div className="notification-column-body">
        {notifications.length === 0 ? (
          <EmptyState
            className="notification-column-empty"
            iconClass="mgc mgc_notification_fill"
            title={`No ${title.toLowerCase()} notifications`}
            description=""
          />
        ) : (
          notifications.map((notification) => (
            <NotificationCard
              key={notification.id}
              priority={notification.priority}
              title={notification.title}
              date={notification.date}
              time={notification.time}
              message={notification.message}
              actionButtonText={notification.actionButtonText}
              patient_id={notification.patient_id}
              isRead={readNotifications.has(notification.id)}
              onMarkAsRead={() => onMarkAsRead?.(notification.id)}
              onAction={() => onAction?.(notification.id)}
            />
          ))
        )}
      </div>
    </div>
  )
}

export default NotificationColumn
